// src/services/aiService.js
// --- UPDATE (ML Ops) -----------------------------------------------------------
// Every model-management call now goes through the ML control plane (/api/v1/ml-ops, see
// mlOpsService.js) or a legacy /ai route that the backend now delegates to it. Signatures and
// response shapes are unchanged, so DispatcherTrainingWidget, NlpPartPredictorWidget,
// RulModelInsightsWidget, ExecutiveOverviewDashboard and the review pages keep working — but
// they now get the control plane's guarantees (one run per model, champion/challenger,
// safeguards, one production version, cache flush, audit, live broadcast).
//
// What changed for callers
//   • getModels / getAvailableModels → { success, models, data, total } (paged registry read).
//     They also prime a 60 s metrics cache, so getModelMetrics(type, version) for a model you
//     just listed costs no request (NlpPartPredictorWidget made one request per model).
//   • promoteModel → safeguard-checked; pass { force: true } as a 3rd argument to override.
//     Errors carry .code === 'SAFEGUARD' and .verdict.
//   • deleteModel / rollbackModel / compareModels / getModelDetails / getModelPerformanceMetrics
//     → these routes never existed under /ai (404); they now work through /ml-ops.
//   • triggerPartPredictorTraining / triggerIssueClassifierTraining → start a tracked run and,
//     by default, wait for its outcome (the widgets show "Training Completed" afterwards).
//     Pass { wait: false } to return as soon as the run is queued; onProgress(job) for live progress.
//   • getModelConfig / updateModelConfig → the model's autopilot policy.
//   • explainPrediction → Ask FlowSense AI, focused on the model.
//   • submitPredictionGrade(predictionId, sourceTable, isCorrect, notes) — also accepts an object
//     or the older (predictionId, isCorrect, notes) form. The verifier is the signed-in user
//     (the server ignores client-supplied user ids).
//   • downloadModel → the model card (registry metadata, metrics, history) as JSON; model
//     weights live in the ML engine and are not downloadable through the API.
//   • uploadModel → rejected with a clear message (there is no upload route; train instead).
//
// Not backed by any route in aiRoutes.js today (they still 404 until the backend adds them):
//   testPartPredictor, getPartPredictor* (now mapped), predictRUL, getFailureProbability,
//   detectAnomalies, getAnomalyHistory, extractEntities, analyzeSentiment, classifyPriority,
//   getAIUsageStats, summarizeTechnicalNotes. They are left unchanged.
// --- END UPDATE ------------------------------------------------------------------
import { authenticatedFetch } from './api';
// --- UPDATE (ML Ops) ---
import mlOpsService from './mlOpsService';
// --- END UPDATE ---

// --- UPDATE (ML Ops) -----------------------------------------------------------
const METRICS_TTL_MS = 60 * 1000;
const _metricsCache = new Map(); // key → { at, metrics }

const parseJson = (v, fallback = {}) => {
  if (v === null || v === undefined || v === '') return fallback;
  if (typeof v === 'object') return v;
  try { return JSON.parse(v); } catch (_) { return fallback; }
};
const normType = (t) => {
  const k = String(t || '').trim().toUpperCase().replace(/[\s-]+/g, '_');
  if (k === 'DISPATCH' || k === 'DISPATCH_OPTIMIZATION' || k === 'DISPATCH_OPTIMIZER') return 'DISPATCHER';
  if (k === 'RUL' || k === 'PREDICTIVE_FAILURE') return 'RUL_PREDICTION';
  if (k === 'PART_PREDICTOR' || k === 'PARTS' || k === 'NLP') return 'PART_PREDICTION';
  if (k === 'ISSUE' || k === 'CLASSIFIER') return 'ISSUE_CLASSIFIER';
  return k;
};
function primeMetrics(models = []) {
  const now = Date.now();
  for (const m of models) {
    const metrics = parseJson(m.performance_metrics, {});
    if (m.id !== undefined && m.id !== null) _metricsCache.set(`id:${m.id}`, { at: now, metrics });
    if (m.model_type && m.model_version) _metricsCache.set(`${normType(m.model_type)}|${m.model_version}`, { at: now, metrics });
  }
  if (_metricsCache.size > 2000) _metricsCache.clear();
}
const cachedMetrics = (key) => {
  const hit = _metricsCache.get(key);
  return hit && Date.now() - hit.at < METRICS_TTL_MS ? hit.metrics : null;
};
/** Registry read in the legacy shape every widget understands. */
async function listRegistry({ modelType, status, limit = 100, offset = 0 } = {}) {
  const r = await mlOpsService.listModels({ modelType: modelType ? normType(modelType) : undefined, status, limit, offset });
  const items = Array.isArray(r?.items) ? r.items : [];
  primeMetrics(items);
  return { success: true, models: items, data: items, total: r?.total ?? items.length };
}
const label = (t) => ({ DISPATCHER: 'AI Dispatcher', ISSUE_CLASSIFIER: 'Issue Classifier', PART_PREDICTION: 'Part Predictor', RUL_PREDICTION: 'RUL model' }[normType(t)] || t);
// --- END UPDATE ------------------------------------------------------------------

// ============================================================================
// PART PREDICTOR SERVICES
// ============================================================================

/**
 * Trigger training for the part predictor model
 * --- UPDATE (ML Ops): tracked control-plane run; waits for the outcome unless { wait: false }.
 * @param {{ wait?: boolean, onProgress?: Function, signal?: AbortSignal }} [options]
 * @returns {Promise} { jobId, status, decision, model_version, message }
 */
export const triggerPartPredictorTraining = async ({ wait = true, onProgress, signal } = {}) => {
  try {
    if (!wait) {
      const job = await mlOpsService.train('PART_PREDICTION');
      return { jobId: job.id, status: 'running', message: job.alreadyActive ? 'The Part Predictor is already training.' : 'Part Predictor training started.', job };
    }
    return await mlOpsService.trainAndWait('PART_PREDICTION', { onProgress, signal });
  } catch (error) {
    console.error('[AI Service] Error triggering part predictor training:', error);
    throw error;
  }
};

/**
 * Trigger NLP processing for technician feedback
 * @param {Object} options - Processing options
 * @returns {Promise} Processing response
 */
export const triggerNlpProcessing = async (options = {}) => {
  try {
    const endpoint = '/ai/process-nlp-feedback';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        dateRange: options.dateRange || 'last_30_days',
        minMentions: options.minMentions || 3,
        processingMode: options.processingMode || 'incremental',
      }),
    });
  } catch (error) {
    console.error('[AI Service] Error processing NLP feedback:', error);
    throw error;
  }
};

/**
 * Trigger issue classifier training
 * --- UPDATE (ML Ops): the old /ai/train-issue-classifier path did not exist (404).
 * @returns {Promise} Training response
 */
export const triggerIssueClassifierTraining = async ({ wait = true, onProgress, signal } = {}) => {
  try {
    if (!wait) {
      const job = await mlOpsService.train('ISSUE_CLASSIFIER');
      return { jobId: job.id, status: 'running', message: job.alreadyActive ? 'The Issue Classifier is already training.' : 'Issue Classifier training started.', job };
    }
    return await mlOpsService.trainAndWait('ISSUE_CLASSIFIER', { onProgress, signal });
  } catch (error) {
    console.error('[AI Service] Error triggering issue classifier training:', error);
    throw error;
  }
};

/**
 * Get part predictor model status and metrics
 * --- UPDATE (ML Ops): production version, history, drift, policy and data volume.
 * @returns {Promise} Model status information
 */
export const getPartPredictorStatus = async () => {
  try {
    return await mlOpsService.getModelDetail('PART_PREDICTION');
  } catch (error) {
    console.error('[AI Service] Error fetching part predictor status:', error);
    throw error;
  }
};

/**
 * Get training history for part predictor
 * --- UPDATE (ML Ops): real training runs.
 * @param {number} limit - Number of history records to return
 * @returns {Promise} Training history array
 */
export const getPartPredictorHistory = async (limit = 20) => {
  try {
    return await mlOpsService.listJobs({ modelType: 'PART_PREDICTION', limit });
  } catch (error) {
    console.error('[AI Service] Error fetching part predictor history:', error);
    throw error;
  }
};

/**
 * Test part predictor with a sample description
 * @param {string} description - Work order description
 * @returns {Promise} Predicted parts
 */
export const testPartPredictor = async (description) => {
  try {
    const endpoint = '/ai/part-predictor/predict';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ description }),
    });
  } catch (error) {
    console.error('[AI Service] Error testing part predictor:', error);
    throw error;
  }
};

// ============================================================================
// RUL MODEL SERVICES
// ============================================================================

/**
 * Get all models with filtering
 * @param {Object} params - Query parameters
 * @param {string} params.modelType - Type of model (e.g., 'RUL_PREDICTION')
 * @param {string} params.status - Model status ('staging', 'production', 'archived')
 * @param {number} params.limit - Maximum number of models to return
 * @returns {Promise} { success, models, data, total }
 */
export const getModels = async (params = {}) => {
  try {
    return await listRegistry({ modelType: params.modelType, status: params.status, limit: params.limit || 100, offset: params.offset || 0 });
  } catch (error) {
    console.error('[AI Service] Error fetching models:', error);
    throw error;
  }
};

/**
 * Get specific model details
 * @param {string} modelType - Type of model
 * @param {string} modelVersion - Model version
 * @returns {Promise} Model details (registry row)
 */
export const getModelDetails = async (modelType, modelVersion) => {
  try {
    const { models } = await listRegistry({ modelType, limit: 200 });
    const model = models.find(m => String(m.model_version) === String(modelVersion));
    if (!model) {
      const e = new Error(`${label(modelType)} version ${modelVersion} was not found.`);
      e.status = 404;
      throw e;
    }
    return model;
  } catch (error) {
    console.error('[AI Service] Error fetching model details:', error);
    throw error;
  }
};

/**
 * Promote a model from staging to production
 * @param {string} modelType - Type of model
 * @param {string} modelVersion - Model version to promote
 * @param {{ force?: boolean, notes?: string }} [options] - force overrides the safeguard
 * @returns {Promise} { success, message, model, previous, verdict }
 */
export const promoteModel = async (modelType, modelVersion, { force = false, notes } = {}) => {
  try {
    const r = await mlOpsService.promote(normType(modelType), modelVersion, { force, notes });
    return {
      success: true,
      ...r,
      message: r?.alreadyProduction
        ? `${label(modelType)} ${modelVersion} is already in production.`
        : `${label(modelType)} ${modelVersion} is now in production${r?.previous ? `, replacing ${r.previous.model_version}` : ''}.`,
    };
  } catch (error) {
    console.error('[AI Service] Error promoting model:', error);
    throw error;
  }
};

// --- UPDATE: Add widget-specific promotion method ---
/**
 * Promote a model using just the ID (Used by NlpPartPredictorWidget)
 * The backend route now promotes through the ML control plane.
 * @param {string} modelId - The unique ID of the model
 */
export const promoteModelToProduction = async (modelId, { force = false } = {}) => {
  try {
    const endpoint = `/ai/models/${encodeURIComponent(modelId)}/promote`;
    return await authenticatedFetch(endpoint, { method: 'POST', body: JSON.stringify({ force }) });
  } catch (error) {
    console.error('[AI Service] Error promoting model by ID:', error);
    throw error;
  }
};
// --- END UPDATE ---

/**
 * Delete a staged model (admins; the production version can never be deleted)
 * @param {string} modelType - Type of model
 * @param {string} modelVersion - Model version to delete
 * @returns {Promise} Deletion response
 */
export const deleteModel = async (modelType, modelVersion) => {
  try {
    const r = await mlOpsService.remove(normType(modelType), modelVersion);
    _metricsCache.delete(`${normType(modelType)}|${modelVersion}`);
    return { success: true, ...r, message: `${label(modelType)} ${modelVersion} deleted.` };
  } catch (error) {
    console.error('[AI Service] Error deleting model:', error);
    throw error;
  }
};

/**
 * Rollback to a previous model version.
 * With a version: that version becomes production. Without: the previous production version returns.
 * @param {string} modelType - Type of model
 * @param {string} [modelVersion] - Model version to roll back to
 * @returns {Promise} Rollback response
 */
export const rollbackModel = async (modelType, modelVersion) => {
  try {
    if (modelVersion) {
      return await promoteModel(modelType, modelVersion, { notes: `Rolled back to ${modelVersion}` });
    }
    const r = await mlOpsService.rollback(normType(modelType));
    return { success: true, ...r, message: `${label(modelType)} rolled back to ${r?.model?.model_version || 'the previous version'}.` };
  } catch (error) {
    console.error('[AI Service] Error rolling back model:', error);
    throw error;
  }
};

/**
 * Compare two models
 * @param {string} modelType - Type of model
 * @param {string} version1 - First model version
 * @param {string} [version2] - Second model version (default: production)
 * @returns {Promise} { a, b, metrics[], verdict }
 */
export const compareModels = async (modelType, version1, version2) => {
  try {
    return await mlOpsService.compare(normType(modelType), version1, version2);
  } catch (error) {
    console.error('[AI Service] Error comparing models:', error);
    throw error;
  }
};

/**
 * Get model performance metrics over time
 * --- UPDATE (ML Ops): the primary metric for every version of the model (newest 40).
 * @param {string} modelType - Type of model
 * @param {string} [modelVersion] - Narrows to one version when given
 * @param {string} [timeRange] - '7d' | '30d' | '90d' | 'all'
 * @returns {Promise} { metric, series: [{ version, status, metric, createdAt, activatedAt, metrics }] }
 */
export const getModelPerformanceMetrics = async (modelType, modelVersion, timeRange = '30d') => {
  try {
    const d = await mlOpsService.getModelDetail(normType(modelType));
    const days = { '7d': 7, '30d': 30, '90d': 90 }[timeRange] || null;
    const since = days ? Date.now() - days * 86400000 : 0;
    let series = (d?.history || []).filter(h => !since || new Date(h.createdAt).getTime() >= since);
    if (modelVersion) series = series.filter(h => String(h.version) === String(modelVersion));
    return { metric: d?.metric || null, series };
  } catch (error) {
    console.error('[AI Service] Error fetching model performance metrics:', error);
    throw error;
  }
};

// ============================================================================
// PREDICTIVE MAINTENANCE SERVICES
// ============================================================================

/**
 * Get RUL prediction for specific equipment
 * @param {string} equipmentId - Equipment ID
 * @param {Object} sensorData - Current sensor readings
 * @returns {Promise} RUL prediction
 */
export const predictRUL = async (equipmentId, sensorData = {}) => {
  try {
    const endpoint = '/ai/predict-rul';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipmentId, sensorData }),
    });
  } catch (error) {
    console.error('[AI Service] Error predicting RUL:', error);
    throw error;
  }
};

/**
 * Get batch RUL predictions for multiple equipment
 * @param {Array<string>} equipmentIds - Array of equipment IDs
 * @returns {Promise} Batch predictions
 */
export const predictRULBatch = async (equipmentIds) => {
  try {
    const endpoint = '/ai/predict-rul/batch';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipmentIds }),
    });
  } catch (error) {
    console.error('[AI Service] Error predicting RUL batch:', error);
    throw error;
  }
};

/**
 * Get failure probability for equipment
 * @param {string} equipmentId - Equipment ID
 * @param {number} horizon - Prediction horizon in days
 * @returns {Promise} Failure probability
 */
export const getFailureProbability = async (equipmentId, horizon = 30) => {
  try {
    const params = new URLSearchParams({ horizon: horizon.toString() });
    const endpoint = `/ai/failure-probability/${equipmentId}?${params.toString()}`;
    return await authenticatedFetch(endpoint);
  } catch (error) {
    console.error('[AI Service] Error fetching failure probability:', error);
    throw error;
  }
};

// ============================================================================
// 🌊 HYDROLOGICAL & WEATHER FLOOD RISK ANALYTICS
// ============================================================================

/**
 * Evaluates predictive flood analytics combining telemetry velocity, silt benchmarks, and weather
 * @param {string} wellId The asset ID of the well
 * @param {number} [hazardRadiusMeters=400] Scanning radius in meters
 * @returns {Promise<Object>} Prediction data with probability, time-to-overtop, and weather impact
 */
export const evaluateWellFloodAnalytics = async (wellId, hazardRadiusMeters = 400) => {
  try {
    const endpoint = '/ai/flood/evaluate';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ wellId, hazardRadiusMeters }),
    });
  } catch (error) {
    console.error(`[AI Service] Error evaluating flood analytics for well ${wellId}:`, error);
    throw error;
  }
};

// ============================================================================
// ANOMALY DETECTION SERVICES
// ============================================================================

/**
 * Detect anomalies in equipment sensor data
 * @param {string} equipmentId - Equipment ID
 * @param {Object} data - Sensor data for analysis
 * @returns {Promise} Anomaly detection results
 */
export const detectAnomalies = async (equipmentId, data) => {
  try {
    const endpoint = '/ai/detect-anomalies';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ equipmentId, data }),
    });
  } catch (error) {
    console.error('[AI Service] Error detecting anomalies:', error);
    throw error;
  }
};

/**
 * Get anomaly history for equipment
 * @param {string} equipmentId - Equipment ID
 * @param {Object} dateRange - Date range for history
 * @returns {Promise} Anomaly history
 */
export const getAnomalyHistory = async (equipmentId, dateRange = {}) => {
  try {
    const params = new URLSearchParams();
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);

    const endpoint = `/ai/anomalies/${equipmentId}?${params.toString()}`;
    return await authenticatedFetch(endpoint);
  } catch (error) {
    console.error('[AI Service] Error fetching anomaly history:', error);
    throw error;
  }
};

// ============================================================================
// TEXT ANALYSIS SERVICES
// ============================================================================

/**
 * Extract entities from text
 * @param {string} text - Text to analyze
 * @returns {Promise} Extracted entities
 */
export const extractEntities = async (text) => {
  try {
    const endpoint = '/ai/extract-entities';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
  } catch (error) {
    console.error('[AI Service] Error extracting entities:', error);
    throw error;
  }
};

/**
 * Perform sentiment analysis on text
 * @param {string} text - Text to analyze
 * @returns {Promise} Sentiment analysis results
 */
export const analyzeSentiment = async (text) => {
  try {
    const endpoint = '/ai/analyze-sentiment';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text }),
    });
  } catch (error) {
    console.error('[AI Service] Error analyzing sentiment:', error);
    throw error;
  }
};

/**
 * Classify work order priority using AI
 * @param {Object} workOrder - Work order data
 * @returns {Promise} Priority classification
 */
export const classifyPriority = async (workOrder) => {
  try {
    const endpoint = '/ai/classify-priority';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(workOrder),
    });
  } catch (error) {
    console.error('[AI Service] Error classifying priority:', error);
    throw error;
  }
};

// ============================================================================
// MODEL MANAGEMENT & MONITORING
// ============================================================================

/**
 * Get model drift metrics
 * --- UPDATE: /ai/models/:type/:version/drift never existed; the ML engine's drift report is
 * served by /ml/drift/:modelType (production version vs its training baseline).
 * @param {string} modelType - Type of model
 * @returns {Promise} Drift report
 */
export const getModelDrift = async (modelType) => {
  try {
    const res = await authenticatedFetch(`/ml/drift/${encodeURIComponent(normType(modelType))}`);
    return res?.data !== undefined ? res.data : res;
  } catch (error) {
    console.error('[AI Service] Error fetching model drift:', error);
    throw error;
  }
};

/**
 * Get model explanation for a prediction
 * --- UPDATE: answered by Ask FlowSense AI with the model in focus (grounded in the live
 * registry and catalog). Returns { answer, source, model, topics, suggestions }.
 * @param {string} modelType - Type of model
 * @param {Object} input - Input data / prediction to explain
 * @returns {Promise} Explanation of prediction
 */
export const explainPrediction = async (modelType, input) => {
  try {
    const sample = JSON.stringify(input ?? {}).slice(0, 700);
    return await mlOpsService.ask({
      question: `Explain how the ${label(modelType)} reaches a prediction like this one and what the result means for staff: ${sample}`,
      focus: normType(modelType),
    });
  } catch (error) {
    console.error('[AI Service] Error explaining prediction:', error);
    throw error;
  }
};

/**
 * Get system-wide AI health metrics
 * @returns {Promise} AI system health (ML engine reachability is now real, not hard-coded)
 */
export const getAIHealthMetrics = async () => {
  try {
    const endpoint = '/ai/health';
    return await authenticatedFetch(endpoint);
  } catch (error) {
    console.error('[AI Service] Error fetching AI health metrics:', error);
    throw error;
  }
};

/**
 * Get AI usage statistics
 * @param {Object} dateRange - Date range for statistics
 * @returns {Promise} Usage statistics
 */
export const getAIUsageStats = async (dateRange = {}) => {
  try {
    const params = new URLSearchParams();
    if (dateRange.startDate) params.append('startDate', dateRange.startDate);
    if (dateRange.endDate) params.append('endDate', dateRange.endDate);

    const endpoint = `/ai/usage-stats?${params.toString()}`;
    return await authenticatedFetch(endpoint);
  } catch (error) {
    console.error('[AI Service] Error fetching AI usage stats:', error);
    throw error;
  }
};

// ============================================================================
// CONFIGURATION & UTILITIES
// ============================================================================

/**
 * Update model configuration
 * --- UPDATE (ML Ops): the model's autopilot / promotion policy
 * ({ enabled, autoPromote, minNewRecords, minIntervalHours, improvementThreshold, safeguard, keepArchived }).
 * @param {string} modelType - Type of model
 * @param {Object} config - Configuration object
 * @returns {Promise} The saved policy
 */
export const updateModelConfig = async (modelType, config) => {
  try {
    return await mlOpsService.setPolicy(normType(modelType), config || {});
  } catch (error) {
    console.error('[AI Service] Error updating model config:', error);
    throw error;
  }
};

/**
 * Get model configuration
 * --- UPDATE (ML Ops): the model's autopilot / promotion policy.
 * @param {string} modelType - Type of model
 * @returns {Promise} Model policy
 */
export const getModelConfig = async (modelType) => {
  try {
    const p = await mlOpsService.getPolicies();
    return p?.models?.[normType(modelType)] || null;
  } catch (error) {
    console.error('[AI Service] Error fetching model config:', error);
    throw error;
  }
};

/**
 * Download model artifacts
 * --- UPDATE: returns the model card (registry metadata, metrics, feature importance and the
 * model's version history) as a JSON Blob. The previous version fetched a relative URL without
 * the API base and with the wrong token key, so it always failed.
 * @param {string} modelType - Type of model
 * @param {string} modelVersion - Model version
 * @returns {Promise<Blob>} application/json model card
 */
export const downloadModel = async (modelType, modelVersion) => {
  try {
    const [model, detail] = await Promise.all([
      getModelDetails(modelType, modelVersion),
      mlOpsService.getModelDetail(normType(modelType)).catch(() => null),
    ]);
    const card = {
      kind: 'flowsense-model-card',
      exportedAt: new Date().toISOString(),
      model,
      metric: detail?.metric || null,
      catalog: detail?.catalog || null,
      history: detail?.history || [],
      policy: detail?.policy || null,
    };
    return new Blob([JSON.stringify(card, null, 2)], { type: 'application/json' });
  } catch (error) {
    console.error('[AI Service] Error downloading model:', error);
    throw error;
  }
};

/**
 * Upload model artifacts
 * --- UPDATE: there is no upload route — models are trained by the ML engine from FlowSense
 * data so their metrics are comparable with production. Fails fast with that explanation.
 */
export const uploadModel = async () => {
  const e = new Error('Uploading model files is not supported. Train a new version from the AI Intelligence Center — it is compared with production automatically.');
  e.status = 501;
  throw e;
};

/**
 * Fetches work orders where AI predictions differ from actual usage
 * for manual verification and inclusion in the Golden Dataset.
 * @returns {Promise<{ queue: Array, verifiedCount: number, pendingCount: number }>}
 */
export const getReviewQueue = async () => {
  try {
    const endpoint = '/ai/review-queue';
    const data = await authenticatedFetch(endpoint);

    // --- UPDATE: Return an object containing both the queue array and the lifetime verified count
    const queue = Array.isArray(data?.queue) ? data.queue : (Array.isArray(data) ? data : []);
    return {
      queue,
      verifiedCount: data?.verified_count || 0,
      pendingCount: data?.pending_count ?? queue.length,
    };
    // --- END UPDATE
  } catch (error) {
    console.error('[AI Service] Error fetching review queue:', error);
    // Return empty payload on fail so UI doesn't crash
    return { queue: [], verifiedCount: 0, pendingCount: 0 };
  }
};

/**
 * Submits human-verified ground truth for a specific prediction.
 * --- UPDATE: one signature for every caller —
 *   submitPredictionGrade(predictionId, sourceTable, isCorrect, notes)
 *   submitPredictionGrade({ predictionId, sourceTable, isCorrect, notes })
 *   submitPredictionGrade(predictionId, isCorrect, notes)        (older form, no source table)
 * The server records the signed-in user as the verifier.
 */
export const submitPredictionGrade = async (predictionIdOrPayload, sourceTable, isCorrect, notes) => {
  let p;
  if (predictionIdOrPayload && typeof predictionIdOrPayload === 'object') p = predictionIdOrPayload;
  else if (typeof sourceTable === 'boolean') p = { predictionId: predictionIdOrPayload, sourceTable: undefined, isCorrect: sourceTable, notes: isCorrect };
  else p = { predictionId: predictionIdOrPayload, sourceTable, isCorrect, notes };

  if (!p.sourceTable) {
    const e = new Error('This prediction has no source table, so it cannot be graded. Refresh the review queue.');
    e.status = 400;
    throw e;
  }
  try {
    const endpoint = '/ai/grade-prediction';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        predictionId: p.predictionId,
        sourceTable: p.sourceTable,
        isCorrect: Boolean(p.isCorrect),
        notes: p.notes || null,
        clientSystem: 'FlowSense-Admin-v3'
      }),
    });
  } catch (error) {
    console.error(`[AI Service] Error grading prediction ID #${p.predictionId}:`, error);
    throw error;
  }
};
// --- END UPDATE ---


// ============================================================================
// --- NEW: MISSING DISPATCHER & TRAINING METHODS ---
// ============================================================================

// --- UPDATE: Support filtering by modelType ---
/**
 * Get list of available AI models (Required by DispatcherTrainingWidget)
 * Supports filtering by modelType to avoid loading unnecessary models.
 * @param {Object} filters - { modelType: 'PART_PREDICTION', status: 'staging' }
 * @returns {Promise} { success, models, data, total }
 */
export const getAvailableModels = async (filters = {}) => {
  try {
    return await listRegistry({ modelType: filters.modelType, status: filters.status, limit: filters.limit || 100 });
  } catch (error) {
    console.error('[AI Service] Error fetching available models:', error);
    throw error;
  }
};
// --- END UPDATE ---

/**
 * Get historical training runs (Required by NlpPartPredictorWidget)
 * The backend now returns real control-plane runs: { success, history: [...] }.
 * @param {{ modelType?: string, limit?: number }} [options]
 * @returns {Promise} Training history
 */
export const getTrainingHistory = async ({ modelType, limit } = {}) => {
  try {
    const params = new URLSearchParams();
    if (modelType) params.append('modelType', normType(modelType));
    if (limit) params.append('limit', String(limit));
    const qs = params.toString();
    return await authenticatedFetch(`/ai/training/history${qs ? `?${qs}` : ''}`);
  } catch (error) {
    console.error('[AI Service] Error fetching training history:', error);
    throw error;
  }
};

/**
 * Trigger a new training run (Generic)
 * --- UPDATE (ML Ops): queues a tracked run; config.note is recorded on it.
 * @param {string} modelType - Type of model to train
 * @param {Object} config - { note?, autoPromote?, wait?, onProgress? }
 * @returns {Promise} The queued job, or the outcome when config.wait is true
 */
export const trainModel = async (modelType, config = {}) => {
  try {
    if (config.wait) return await mlOpsService.trainAndWait(normType(modelType), { note: config.note, autoPromote: config.autoPromote, onProgress: config.onProgress });
    return await mlOpsService.train(normType(modelType), { note: config.note, autoPromote: config.autoPromote });
  } catch (error) {
    console.error('[AI Service] Error starting model training:', error);
    throw error;
  }
};

/**
 * Get metrics for a specific model.
 * Supports both (type, version) for general use AND (modelId) for widget use.
 * --- UPDATE: answered from the cache primed by getModels() when possible (no request).
 */
export const getModelMetrics = async (modelTypeOrId, modelVersion) => {
  try {
    const byId = modelVersion === undefined && /^\d+$/.test(String(modelTypeOrId));
    const key = byId ? `id:${modelTypeOrId}` : `${normType(modelTypeOrId)}|${modelVersion || 'latest'}`;
    const hit = modelVersion !== 'latest' ? cachedMetrics(key) : null;
    if (hit) return hit;

    const endpoint = byId
      ? `/ai/models/${encodeURIComponent(modelTypeOrId)}/metrics`
      : `/ai/models/${encodeURIComponent(normType(modelTypeOrId))}/${encodeURIComponent(modelVersion || 'latest')}/metrics`;
    const metrics = await authenticatedFetch(endpoint);
    _metricsCache.set(key, { at: Date.now(), metrics: metrics || {} });
    return metrics || {};
  } catch (error) {
    console.error('[AI Service] Error fetching model metrics:', error);
    throw error;
  }
};

// ============================================================================
// GENERATIVE AI (LLM) SERVICES
// ============================================================================

/**
 * Generates an AI draft email for a citizen regarding a completed work order.
 * @param {string} workOrderId - The ID of the work order.
 * @returns {Promise<string>} The drafted email text.
 */
export const generateCitizenEmailDraft = async (workOrderId) => {
  try {
    const endpoint = `/work-orders/${workOrderId}/generate-citizen-update`;
    const response = await authenticatedFetch(endpoint, { method: 'POST' });
    return response?.draft_email || response;
  } catch (error) {
    console.error(`[AI Service] Error generating email draft for WO #${workOrderId}:`, error);
    throw error;
  }
};

/**
 * Summarizes a long technician chat thread or complex resolution notes.
 * (Ready for your future LLM expansion)
 * @param {string} textToSummarize - The raw text.
 * @returns {Promise<string>} The summary.
 */
export const summarizeTechnicalNotes = async (textToSummarize) => {
  try {
    const endpoint = '/ai/generate-summary';
    return await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: textToSummarize }),
    });
  } catch (error) {
    console.error('[AI Service] Error summarizing text:', error);
    throw error;
  }
};

export const generateHelpdeskReplyDraft = async (ticketId) => {
    const endpoint = `/helpdesk/${ticketId}/generate-reply`;
    const response = await authenticatedFetch(endpoint, { method: 'POST' });
    return response?.draft || response;
};

/**
 * Generates an AI-drafted budget justification memo for a purchase order.
 * @param {string|number} itemId - The ID of the inventory item.
 * @param {number} orderQuantity - How many units are being ordered.
 * @returns {Promise<Object>} The generated draft and supporting metrics.
 */
export const generatePOJustification = async (itemId, orderQuantity) => {
  try {
    const endpoint = '/ai/generate-po-justification';
    const response = await authenticatedFetch(endpoint, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ itemId, orderQuantity }),
    });
    return response; // Returns { success, draft, metrics }
  } catch (error) {
    console.error('[AI Service] Error generating PO justification:', error);
    throw error;
  }
};

/**
 * Predictive Procurement: Analyzes database views to suggest what needs ordering.
 */
export const getReorderSuggestions = async () => {
    try {
        // --- USING authenticatedFetch to match the rest of the file ---
        const endpoint = '/inventory/ai/reorder-suggestions';
        const response = await authenticatedFetch(endpoint);
        return response; // authenticatedFetch returns the parsed JSON body
    } catch (error) {
        console.error("[AI Service] Error fetching reorder suggestions:", error);
        throw error;
    }
};

/**
 * Supplier Performance Analytics: Grades vendors based on historical POs.
 */
export const getSupplierHealth = async () => {
    try {
        const endpoint = '/inventory/ai/supplier-health';
        const response = await authenticatedFetch(endpoint);
        return response;
    } catch (error) {
        console.error("[AI Service] Error fetching supplier health:", error);
        throw error;
    }
};

/**
 * Context-Aware Seasonality AI: Analyzes last year's data for the current month
 */
export const getSeasonalForecast = async () => {
    try {
        const endpoint = '/inventory/ai/seasonal-forecast';
        const response = await authenticatedFetch(endpoint);
        return response;
    } catch (error) {
        console.error("[AI Service] Error fetching seasonal forecast:", error);
        throw error;
    }
};

/**
 * AI Supplier Alternative: Drafts an RFQ for a competing supplier due to price creep
 */
export const draftAlternativeQuote = async (itemId, currentSupplierName) => {
    try {
        const endpoint = '/inventory/ai/draft-alternative-quote';
        const response = await authenticatedFetch(endpoint, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ itemId, currentSupplierName }),
        });
        return response;
    } catch (error) {
        console.error("[AI Service] Error drafting alternative quote:", error);
        throw error;
    }
};

// Default export with all methods
const aiService = {
  // Part Predictor
  triggerPartPredictorTraining,
  triggerNlpProcessing,
  triggerIssueClassifierTraining,
  getPartPredictorStatus,
  getPartPredictorHistory,
  testPartPredictor,
  getTrainingHistory,
  trainModel,

  // Model Management
  getModels,
  getModelMetrics,
  getAvailableModels,
  getModelDetails,
  promoteModel,
  promoteModelToProduction, // Export the new function
  deleteModel,
  rollbackModel,
  compareModels,
  getModelPerformanceMetrics,

  // Predictive Maintenance
  predictRUL,
  predictRULBatch,
  getFailureProbability,

  evaluateWellFloodAnalytics,

  // Anomaly Detection
  detectAnomalies,
  getAnomalyHistory,

  // Text Analysis
  extractEntities,
  analyzeSentiment,
  classifyPriority,

  // Monitoring
  getModelDrift,
  explainPrediction,
  getAIHealthMetrics,
  getAIUsageStats,

  // Configuration
  updateModelConfig,
  getModelConfig,
  downloadModel,
  uploadModel,

  // Generative AI
  generateCitizenEmailDraft,
  summarizeTechnicalNotes,
  generateHelpdeskReplyDraft,
  generatePOJustification,
  getSeasonalForecast,
  draftAlternativeQuote,

  // Predictive Procurement & Analytics (NEW)
  getReorderSuggestions,
  getSupplierHealth,

  // Verification
  getReviewQueue,
  submitPredictionGrade
};

export default aiService;
